import ProductsApi from "@/scripts/api/products.api";
import { ProductSlimResponseDTO } from "@shared/DTO";
import { Mount, SensorType, ProductSubCategoryEnum, ProductTypeEnum, ProductUsageEnum } from "@shared/enums";
import { composeProducts } from "./composition/composeProducts";
import {
  EMPTY_PRODUCT_LISTING_SELECTIONS,
  filterProductListingItems,
  pruneProductListingSelections,
} from "./filtering/productFilterEngine";
import { createProductListingItems } from "./variants/productListingItem";
import {
  ProductListingConfig,
  ProductListingItem,
  ProductListingSelections,
  ProductListingState,
  ProductSlimRepository,
} from "./abProducts.types";

type ProductListingStateListener = (state: ProductListingState) => void;
const EMPTY_PRODUCTS: ReadonlyArray<ProductSlimResponseDTO> = Object.freeze([]);
const EMPTY_ITEMS: ReadonlyArray<ProductListingItem> = Object.freeze([]);

const getInitialState = (): ProductListingState => ({
  status: "idle",
  sourceProducts: EMPTY_PRODUCTS,
  composedItems: EMPTY_ITEMS,
  visibleItems: EMPTY_ITEMS,
  selections: EMPTY_PRODUCT_LISTING_SELECTIONS,
});

/** Keeps the cached API pool unchanged and projects local listing items for each instance. */
export class ProductListingController {
  #config: ProductListingConfig;
  #state = getInitialState();
  #loadToken = 0;
  #listeners = new Set<ProductListingStateListener>();

  constructor(
    config: ProductListingConfig,
    private readonly productRepository: ProductSlimRepository = ProductsApi,
  ) {
    this.#config = config;
  }

  get config(): ProductListingConfig { return this.#config; }
  get state(): ProductListingState { return this.#state; }

  subscribe(listener: ProductListingStateListener): () => void {
    this.#listeners.add(listener);
    listener(this.#state);
    return () => this.#listeners.delete(listener);
  }

  async load(): Promise<void> {
    const token = ++this.#loadToken;
    this.#setState({ ...getInitialState(), status: "loading", selections: this.#state.selections });
    try {
      const products = await (this.#config.include
        ? this.productRepository.getProductSlim(this.#config.source, { include: this.#config.include })
        : this.productRepository.getProductSlim(this.#config.source));
      if (token !== this.#loadToken) return;
      this.#setProducts(products);
    } catch (error) {
      if (token !== this.#loadToken) return;
      this.#setState({ ...getInitialState(), status: "error", error });
    }
  }

  async updateConfig(config: ProductListingConfig): Promise<void> {
    const sourceChanged = config.source !== this.#config.source || config.include !== this.#config.include;
    this.#config = config;
    if (sourceChanged) {
      this.#state = { ...this.#state, selections: EMPTY_PRODUCT_LISTING_SELECTIONS };
      await this.load();
      return;
    }
    if (this.#state.status === "ready" || this.#state.status === "empty") {
      this.#setProducts(this.#state.sourceProducts);
    }
  }

  setBrands(brands: readonly string[]) { this.#setSelections({ ...this.#state.selections, brands }); }
  setProductTypes(productTypes: readonly ProductTypeEnum[]) { this.#setSelections({ ...this.#state.selections, productTypes }); }
  setSensorFormats(sensorFormats: readonly SensorType[]) { this.#setSelections({ ...this.#state.selections, sensorFormats }); }
  setCompatibleLensMounts(compatibleLensMounts: readonly Mount[]) {
    this.#setSelections({ ...this.#state.selections, compatibleLensMounts });
  }
  setTargets(targets: readonly number[]) { this.#setSelections({ ...this.#state.selections, targets }); }
  setSubCategories(subCategories: readonly ProductSubCategoryEnum[]) {
    this.#setSelections({ ...this.#state.selections, subCategories });
  }
  setUsages(usages: readonly ProductUsageEnum[]) { this.#setSelections({ ...this.#state.selections, usages }); }
  resetFilters() { this.#setSelections(EMPTY_PRODUCT_LISTING_SELECTIONS); }

  #setSelections(selections: ProductListingSelections) {
    if (this.#state.status === "ready" || this.#state.status === "empty") {
      this.#setVisibleItems(this.#state.sourceProducts, this.#state.composedItems, selections);
    }
  }

  #setProducts(sourceProducts: ReadonlyArray<ProductSlimResponseDTO>) {
    const composedProducts = composeProducts(sourceProducts, this.#config.composition.productIds);
    const composedItems = createProductListingItems(composedProducts);
    this.#setVisibleItems(sourceProducts, composedItems, this.#state.selections);
  }

  #setVisibleItems(
    sourceProducts: ReadonlyArray<ProductSlimResponseDTO>,
    composedItems: ReadonlyArray<ProductListingItem>,
    requestedSelections: ProductListingSelections,
  ) {
    const selections = pruneProductListingSelections(composedItems, this.#config.filters, requestedSelections);
    const visibleItems = filterProductListingItems(composedItems, this.#config.filters, selections);
    this.#setState({
      status: visibleItems.length ? "ready" : "empty",
      sourceProducts: [...sourceProducts],
      composedItems,
      visibleItems,
      selections,
    });
  }

  #setState(state: ProductListingState) {
    this.#state = state;
    this.#listeners.forEach((listener) => listener(this.#state));
  }
}
